import { api } from './api.js';
import { TAG_CONTACT, TAG_LIST_ORGANIZATIONS, TAG_ORGANIZATION, TAG_PROJECT } from './tags.js';
import { convertToUrlSearchParams } from '../utils/helper.js';

export const organizationsEndpoints = api.injectEndpoints({
    endpoints: (builder) => ({
        getOrganizations: builder.query({
            query: ({ search, page, per_page }) => {
                const queryParams = new URLSearchParams();
                if (search) queryParams.append('search', search);
                if (page) queryParams.append('page', page);
                if (per_page) queryParams.append('per_page', per_page);

                return {
                    url: `/organizations?${queryParams.toString()}`,
                    method: 'GET'
                };
            },
            providesTags: [TAG_LIST_ORGANIZATIONS],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        getOrganization: builder.query({
            query: (id) => ({
                url: `/organizations/view/${id}`,
                method: 'GET'
            }),
            providesTags: [TAG_ORGANIZATION],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        editOrganization: builder.mutation({
            query: ({ id, data }) => ({
                url: `/organizations/edit/${id}`,
                method: 'POST',
                body: data
            }),
            invalidatesTags: [TAG_LIST_ORGANIZATIONS, TAG_ORGANIZATION],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        addOrganization: builder.mutation({
            query: (data) => ({
                url: '/organizations/add',
                method: 'POST',
                body: data
            }),
            invalidatesTags: [TAG_LIST_ORGANIZATIONS, TAG_ORGANIZATION],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        getOrganizationOwnershipTypes: builder.query({
            query: () => ({
                url: '/misc/organizations-ownership-types',
                method: 'GET'
            }),
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        deleteOrganization: builder.mutation({
            query: (id) => ({
                url: `/organizations/delete/${id}`,
                method: 'DELETE'
            }),
            invalidatesTags: [TAG_LIST_ORGANIZATIONS],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        massDeleteOrganizations: builder.mutation({
            query: (data) => ({
                url: `/organizations/bulk-delete`,
                method: 'POST',
                body: data
            }),
            invalidatesTags: [TAG_LIST_ORGANIZATIONS, TAG_ORGANIZATION],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        organizationsAdvancedSearch: builder.query({
            query: (params) => ({
                url: `/organizations/search?${convertToUrlSearchParams(params)}`,
                method: 'GET'
            }),
            providesTags: [TAG_LIST_ORGANIZATIONS],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        getOrganizationNews: builder.query({
            query: ({ page, per_page, id } = {}) => {
                const queryParams = new URLSearchParams();
                if (page) queryParams.append('page', page);
                if (per_page) queryParams.append('per_page', per_page);

                return {
                    url: `/organizations/news/${id}?${queryParams.toString()}`,
                    method: 'GET'
                };
            },
            providesTags: [TAG_ORGANIZATION],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        getOrganizationProjects: builder.query({
            query: ({ page, per_page, id } = {}) => {
                const queryParams = new URLSearchParams();
                if (page) queryParams.append('page', page);
                if (per_page) queryParams.append('per_page', per_page);

                return {
                    url: `/organizations/projects/${id}?${queryParams.toString()}`,
                    method: 'GET'
                };
            },
            providesTags: [TAG_ORGANIZATION, TAG_PROJECT],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        getOrganizationContacts: builder.query({
            query: ({ page, per_page, id } = {}) => {
                const queryParams = new URLSearchParams();
                if (page) queryParams.append('page', page);
                if (per_page) queryParams.append('per_page', per_page);

                return {
                    url: `/organizations/contacts/${id}?${queryParams.toString()}`,
                    method: 'GET'
                };
            },
            providesTags: [TAG_ORGANIZATION, TAG_CONTACT],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        getOrganizationsDashboard: builder.query({
            query: (params) => ({
                url: `/organizations/dashboard/?${convertToUrlSearchParams(params)}`,
                method: 'GET'
            }),
            providesTags: [TAG_LIST_ORGANIZATIONS],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        getOrganizationKPIs: builder.query({
            query: ({ id, interval }) => {
                const queryParams = new URLSearchParams();
                if (interval) queryParams.append('interval', interval);

                return {
                    url: `/organizations/stock-data/${id}?${queryParams.toString()}`,
                    method: 'GET'
                };
            },
            providesTags: [TAG_ORGANIZATION],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        }),
        getStockSymbols: builder.query({
            query: (params) => ({
                url: `/misc/search-stock-symbols?${convertToUrlSearchParams(params)}`,
                method: 'GET'
            }),
            providesTags: [TAG_LIST_ORGANIZATIONS],
            async onQueryStarted(_, { queryFulfilled }) {
                await queryFulfilled;
            }
        })
    })
});

export const {
    useLazyGetOrganizationsQuery,
    useGetOrganizationQuery,
    useEditOrganizationMutation,
    useAddOrganizationMutation,
    useGetOrganizationOwnershipTypesQuery,
    useDeleteOrganizationMutation,
    useMassDeleteOrganizationsMutation,
    useLazyOrganizationsAdvancedSearchQuery,
    useGetOrganizationNewsQuery,
    useGetOrganizationProjectsQuery,
    useGetOrganizationContactsQuery,
    useLazyGetOrganizationsDashboardQuery,
    useGetOrganizationKPIsQuery,
    useLazyGetStockSymbolsQuery
} = organizationsEndpoints;
